import { useEffect, useRef, useState } from "react";
import {
  Skeleton,
  Typography,
  Container,
  Box,
  useMediaQuery,
} from "@mui/material";

import Filters from "./Sections/Filters";
import TopPlayers from "./Sections/Tops/TopPlayers";
import TopTeams from "./Sections/Tops/TopTeams";
import TopCollectionsQty from "./Sections/Tops/TopCollectionsQty";

import ItemsList from "../../components/ItemsList/ItemsList";
import PageControllers from "../../components/PageControllers/PageControllers";
import MenuService from "../../components/Menu/menuService";
import BackdropLoaderService from "../../components/BackdropLoader/backdropLoaderService";

import { Shirt } from "../../entities/Shirt";
import { PlayerWithCount, TeamWithCount } from "../../services/shirts/types";
import TopService from "../../services/topService";
import ShirtsService from "../../services/shirts/shirtsService";

import { TopUser } from "../../types/authTypes";
import { Dictionary } from "../../types/types";

const Collections = () => {
  const allPlayersPath = "/top/players";
  const allTeamsPath = "/top/teams";
  const allUsersQtyPath = "/top/users/qty";
  const myRef = useRef<null | HTMLDivElement>(null);

  const [shirts, setShirts] = useState<Array<Shirt> | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number | undefined>(undefined);
  const [itemsPerPage, setItemsPerPage] = useState<number>();

  const isMobile = useMediaQuery("(max-width: 768px)");

  const [topPlayers, setTopPlayers] = useState<
    Array<PlayerWithCount> | undefined
  >(undefined);
  const [topTeams, setTeams] = useState<Array<TeamWithCount> | undefined>(
    undefined
  );
  const [topUsersByNumber, setTopUsersByNumber] = useState<
    Array<TopUser> | undefined
  >(undefined);

  const { getTopPlayers, getTopTeams, getTopUsersByNumber } = TopService();
  const { selectPage } = MenuService();
  const { open: openBDL, close: closeBDL } = BackdropLoaderService();
  const { searchShirtsPublic } = ShirtsService();

  const searchByTeam = (teamId: string) => {
    openBDL();
    if (myRef?.current) {
      myRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    let params: Dictionary<string | number> = {};
    params["team_id"] = teamId;

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  const searchByPlayer = (playerId: string) => {
    openBDL();
    if (myRef?.current) {
      myRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    let params: Dictionary<string | number> = {};
    params["player_id"] = playerId;

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  useEffect(() => {
    selectPage("menu-shirts-collections");

    getTopPlayers(5, 1).then((result) => {
      if (result) {
        setTopPlayers(result.data as Array<PlayerWithCount>);
      }
    });
    getTopTeams(5, 1).then((result) => {
      if (result) {
        setTeams(result.data as Array<TeamWithCount>);
      }
    });
    getTopUsersByNumber(5, 1).then((result) => {
      if (result) {
        setTopUsersByNumber(result.data as Array<TopUser>);
      }
    });
  }, []);

  useEffect(() => {
    setItemsPerPage(isMobile ? 10 : 24);
  }, [isMobile]);

  return (
    <Container
      maxWidth={false}
      sx={{ backgroundColor: "#f4f6f8", paddingY: { xs: "2rem", sm: "3rem" } }}
    >
      <Container
        maxWidth="lg"
        sx={{ paddingBottom: "1rem", paddingX: 0, paddingTop: 0 }}
      >
        <Box ref={myRef}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontWeight: "700 !important",
              lineHeight: "1.2 !important",
              textAlign: "left",
              color: "#6f7d95 !important",
              fontSize: { xs: "1.4rem", md: "1.75rem" },
            }}
          >
            Search in collections
          </Typography>
        </Box>

        {itemsPerPage && (
          <Filters
            setShirts={setShirts}
            page={page}
            setLastPage={setLastPage}
            itemsPerPage={itemsPerPage}
            setPage={setPage}
          />
        )}

        {shirts && shirts.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              padding: "0px",
            }}
          >
            <ItemsList
              shirts={shirts}
              showAdd={false}
              justify="center"
              widthProps={{
                xs: "100%",
                sm: "calc(50%)",
                md: "calc(33.33%)",
                lg: "calc(25%)",
              }}
              shouldShowUser={true}
              imgHeight={72}
            />
            {itemsPerPage && (
              <PageControllers
                setPage={setPage}
                currentPage={page}
                lastPage={lastPage}
                withNumbers
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
              />
            )}
          </Box>
        )}

        {shirts && shirts.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "2rem",
              marginTop: "2rem",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontWeight: "400",
                fontSize: { xs: "1rem", md: "1.2rem" },
                color: "#6f7d95",
                textAlign: "center",
              }}
            >
              No shirts found
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            marginTop: 10,
            padding: "0px",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "calc(33.33% - 16px)" },
              mb: { xs: 2, md: 0 },
            }}
          >
            {topPlayers ? (
              <TopPlayers
                pathAll={allPlayersPath}
                players={topPlayers}
                onClick={searchByPlayer}
              />
            ) : (
              <Skeleton variant="rectangular" width={"100%"} height={400} />
            )}
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: "calc(33.33% - 16px)" },
              mb: { xs: 2, md: 0 },
            }}
          >
            {topTeams ? (
              <TopTeams
                pathAll={allTeamsPath}
                teams={topTeams}
                onClick={searchByTeam}
              />
            ) : (
              <Skeleton variant="rectangular" width={"100%"} height={400} />
            )}
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: "calc(33.33% - 16px)" },
              mb: { xs: 2, md: 0 },
            }}
          >
            {topUsersByNumber ? (
              <TopCollectionsQty
                pathAll={allUsersQtyPath}
                users={topUsersByNumber}
              />
            ) : (
              <Skeleton variant="rectangular" width={"100%"} height={400} />
            )}
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default Collections;
